import { NoContent, RequestResponse } from '@/common/types_and_interfaces.ts'
import { parseRequestError } from '@/common/utilities.ts'
import axiosInstance from '@/libs/axios_config.ts'
import { z } from 'zod'

const FlagStatusSchema = z.number().transform((val) => {
  switch (val) {
    case 0:
      return 'In Review' as const
    case 1:
      return 'Resolved' as const
    case 2:
      return 'Rejected' as const
    default:
      throw new Error(`Invalid flag status code: ${val}`)
  }
})

export type FlagStatus = z.infer<typeof FlagStatusSchema>

const mapApplicationStatus = (val: number) => {
  switch (val) {
    case 1:
      return 'Invited' as const
    case 2:
      return 'In Progress' as const
    case 3:
      return 'Pending' as const
    case 4:
      return 'Eligible' as const
    case 5:
      return 'In Review' as const
    case 6:
      return 'Ineligible' as const
    case 7:
      return 'Complete' as const
    case 8:
      return 'Awarded' as const
    case 9:
      return 'Appeal' as const
    case 10:
      return 'Funded' as const
    case 11:
      return 'Wait Listed' as const
    case 12:
      return 'Age Phase Out' as const
    case 13:
      return 'V1 Invited' as const
    case 14:
      return 'ClassWallet Renewal' as const
    case 15:
      return 'SoftPoint Renewal' as const
    case 16:
      return 'Withdrawn' as const
    default:
      throw new Error(`Invalid application status code: ${val}`)
  }
}
const ApplicationStatusSchema = z.number().transform((val) => {
  return mapApplicationStatus(val)
})

export type ApplicationStatus = z.infer<typeof ApplicationStatusSchema>

export const FamilyApplicationEndpoints = {
  update_family_applicant: '/scholar/family_application/applicant_details',
  update_student: '/scholar/family_application/student_details',
  update_family_income: '/scholar/family_application/income',
  get_family_income_tax_commission:
    '/scholar/family_application/document_verification/tax_commission',
  post_tax_commission_user_confirmation:
    '/scholar/family_application/document_verification/tax_commission_user_confirmation',
  update_family_attestation: '/scholar/family_application/attestations',
  submit_family_application: '/scholar/family_application',
  update_scholarship_applications:
    '/scholar/family_application/scholarship_applications',
  post_birth_certificate:
    '/scholar/family_application/document_verification/birth_certificate',
  post_special_education:
    '/scholar/family_application/document_verification/special_education',
  post_relationship:
    '/scholar/family_application/document_verification/relationship',
  post_income_document_verification:
    '/scholar/family_application/document_verification/income',
  file_appeal: '/scholar/family_application/appeals',
  get_user_appeals: '/scholar/appeals_history',
  withdraw_student: '/scholar/family_application/withdraw_student',
  convert_to_new: '/scholar/family_application/new_scholarship_application',
  post_residency_document: '/scholar/family_application/upload-file',
  post_provider_document: '/scholar/provider/upload-temp-file',
  post_ssn_check: '/scholar/family_application/ssn_check',
} as const

export const FamilyApplicationAPIs = {
  getUserApplication: '/scholar/family_account/application',
}
export const RelationToStudentSchema = z.enum(
  ['legal_guardian', 'parent', 'other', 'adoptive_parent'] as const,
  {
    errorMap: () => ({ message: 'Relationship is required' }),
  }
)
export type RelationToStudentSchema = z.infer<typeof RelationToStudentSchema>

export const HouseHoldIncomeTypeSchema = z.enum([
  'single_income',
  'dual_income',
  'other',
])

export type HouseHoldIncomeType = z.infer<typeof HouseHoldIncomeTypeSchema>

const StudentInceptionSchema = z.number().transform((val) => {
  switch (val) {
    case 0:
      return 'New Applicant'
    case 1:
      return 'Waitlisted Applicant'
    case 2:
      return 'Renewal Student'
    case 3:
      return 'Family Renewal Applicant'
    default:
      throw new Error(`Invalid student inception status code: ${val}`)
  }
})

export type StudentInception = z.infer<typeof StudentInceptionSchema>

export const StudentSubStatusSchema = z.number().transform((value) => {
  switch (value) {
    case 1:
      return 'Eligible'
    case 2:
      return 'Selected'
    default:
      throw new Error('Invalid status code ')
  }
})
export type StudentSubStatusSchema = z.infer<typeof StudentSubStatusSchema>

export type ApplicationStatusWithSubStatus =
  | ApplicationStatus
  | 'Selected'
  | 'Ineligible (Duplicate)'
export const StudentSchema = z
  .object({
    id: z.number(),
    student_status: z.number().or(z.undefined()),
    application_status: ApplicationStatusSchema,
    scholarship_application_sub_status: z.number(),
    priority_group: z.number().nullable(),
    inception_status: StudentInceptionSchema,
    date_of_birth: z.string().nullable(),
    scholarship_application_id: z.number().nullable(),
    grade: z.string().nullable(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    middle_name: z.string().nullable().optional(),
    ssn_last4: z.string().nullable(),
    SSID: z.string().max(15).nullable().optional(),
    lea_participation: z.number().nullable(),
    relationship: RelationToStudentSchema.nullable(),
    v1_eligible_for_renewal: z
      .number()
      .transform((val) => {
        // if value is 1 student is eligible for renewal
        // else they are not.
        return val === 1
      })
      .nullable(),
    is_external_student: z.boolean(),
    competing_scholarship_ids: z.array(z.number()).min(0).nullable(),
    birth_certificate_document_id: z.number().nullable(),
    education_plan: z.number().nullable(),
    require_special_education: z.number().nullable(),
    document_name: z.string().nullable(),
    document_description: z.string().nullable(),
    agree_external_document: z.number().nullable(),
  })
  .transform((student) => {
    let statusName: ApplicationStatusWithSubStatus = student.application_status
    if (student.application_status === 'Eligible') {
      switch (student.scholarship_application_sub_status) {
        case 1:
          statusName = 'Eligible'
          break
        case 2:
          statusName = 'Selected'
          break
        default:
          throw new Error('Invalid sub-status code')
      }
    }
    if (student.application_status === 'Ineligible') {
      switch (student.scholarship_application_sub_status) {
        case 1:
          statusName = 'Ineligible'
          break
        case 2:
          statusName = 'Ineligible (Duplicate)'
          break
        default:
          throw new Error('Invalid sub-status code')
      }
    }
    return {
      ...student,
      scholarship_application_sub_status: statusName,
    }
  })
export type Student = z.infer<typeof StudentSchema>

export const getInceptionStatusText = (status: number): string => {
  switch (status) {
    case 0:
      return 'New'
    case 1:
      return 'Waitlist'
    case 2:
      return 'Renewal'
    case 3:
      return 'Family Renewal'
    default:
      return ''
  }
}

export const FamilyAttestationSchema = z.object({
  id: z.number(),
  order: z.number(),
  accepted_time: z.string(),
  status: z.number(),
  step: z.union([
    z.literal('income'),
    z.literal('identity'),
    z.literal('student'),
  ]),
  program_attestation_id: z.number(),
  code: z.string().nullable().optional(),
  student_id: z.number().nullable().optional(),
})
export type FamilyAttestation = z.infer<typeof FamilyAttestationSchema>

const identityFlags = [
  'identity',
  'clearme_data_inaccurate',
  'applicant_update_identity',
] as const
const residencyFlags = ['residency', 'applicant_update_residency'] as const

export const StudentFlags = [
  'student_age',
  'relation_to_student',
  'enrolled_in_lea',
  'enrolled_in_soep',
  'received_another_scholarship',
  'student_age_sep',
  'student_age_affidavit',
  'student_age_special_education',
  'student_external_renewal_ineligible_declaration',
  'applicant_update_student_age',
  'applicant_update_special_education',
  'applicant_update_other',
  'possible_duplicate_student',
] as const

export const IncomeFlags = [
  'tax_commission_skipped_by_applicant',
  'tax_commission_error',
  'tax_commission_applicant_attested_incorrect_data',
  'tax_commission_returned_data_does_not_match',
  'review_income_documents',
  'applicant_update_income',
] as const

export const ExternalRenewalFlags = [
  'student_external_renewal_ineligible_declaration',
] as const

// export type StudentFlag = (typeof StudentFlags)[number]
// export type GeneralFlag = (typeof GeneralFlags)[number]
// export type IncomeFlags = (typeof IncomeFlags)[number]
// export type ExternalRenewalFlag = (typeof ExternalRenewalFlags)[number]

const FlagCodesSchema = z.enum([
  ...identityFlags,
  ...residencyFlags,
  ...StudentFlags,
  ...IncomeFlags,
  ...ExternalRenewalFlags,
])

// Nonfixable flags means that the user can't go in and fix these flags in the application.
// Instead we'll use some kind of third party system to check if these flags should apply.
export const nonFixableFlags = [
  'enrolled_in_soep',
  'student_external_renewal_ineligible_declaration',
  'applicant_update_identity',
  'applicant_update_other',
  'applicant_update_residency',
  'applicant_update_student_age',
  'applicant_update_special_education',
  'applicant_update_income',
  'possible_duplicate_student',
] as const

export type NonFixableFlags = (typeof nonFixableFlags)[number]
export type FixableFlags = Exclude<Flags, NonFixableFlags>

export function isFixableFlag(flagCode: Flags): flagCode is FixableFlags {
  const nonFixableFlag = nonFixableFlags.find((flag) => flag === flagCode)
  const isFixable = !nonFixableFlag

  return isFixable
}

export type Flags = z.infer<typeof FlagCodesSchema>

export function isIdentityFlag(flagCode: ApplicationFlag['flag_code']) {
  return (identityFlags as readonly string[]).includes(flagCode as string)
}
export function isResidencyFlag(flagCode: ApplicationFlag['flag_code']) {
  return (residencyFlags as readonly string[]).includes(flagCode as string)
}

export function isStudentFlag(flagCode: ApplicationFlag['flag_code']) {
  return (StudentFlags as readonly string[]).includes(flagCode as string)
}

export function isTaxIncomeFlag(flagCode: ApplicationFlag['flag_code']) {
  return (IncomeFlags as readonly string[]).includes(flagCode as string)
}

export const ApplicationSectionCodeSchema = z.enum([
  'identity',
  'residency',
  'student',
  'income',
])

export const FlagReviewSchema = z.object({
  id: z.number(),
  status: z.number(),
  // application_id: z.number(),
  // student_id: z.null(),
  // application_flag_id: z.number(),
  global_application_step_id: z.number(),
  application_section: z.number(),
  application_section_code: ApplicationSectionCodeSchema.nullable(),
  // document_id: z.null(),
  // reviewed_by: z.null(),
  // reviewed_at: z.null()
})

export type FlagReview = z.infer<typeof FlagReviewSchema>

const AppealFlagSchema = z.object({
  id: z.number(),
  type: z.number(),
  status: z.number(),
  application_id: z.number(),
  student_id: z.number().nullable(),
  flag_id: z.number(),
  case_number: z.number(),
  reason: z.string(),
  contact_preference: z.null(),
  comments: z.null(),
  review_outcome: z.number(),
  outcome_reasoning: z.number(),
  resolution_message: z.null(),
  expense_denial_date: z.null(),
  created_by: z.null(),
  created_on: z.null(),
  created_datetime: z.string().datetime(),
  last_by: z.null(),
  last_on: z.null(),
  last_datetime: z.string().datetime(),
})

const ApplicationFlagSchema = z.object({
  id: z.number(),
  status: FlagStatusSchema,
  application_id: z.number(),
  student_id: z.number().nullable(),
  student_full_name: z.string().nullable(),
  applicant_full_name: z.string().nullable(),
  flag_code: FlagCodesSchema,
  flag_name: z.string(),
  flag_description: z.string().nullable(),
  reviews: z.array(FlagReviewSchema).min(0),
  appeal: AppealFlagSchema.nullable(),
  hide_flag_on_messages: z.number().nullable(),
  last_message_datetime: z.string().nullable(),
})

export type ApplicationFlag = z.infer<typeof ApplicationFlagSchema>

export const YesOrNoSchema = z.union([z.literal(0), z.literal(1)])

export type YesOrNo = z.infer<typeof YesOrNoSchema>

export const NonTaxableIncomeSchema = z.union([
  z.literal('social_security'),
  z.literal('disability_benefits'),
  z.literal('child_support_payments'),
  z.literal('alimony_payments'),
  z.literal('other'),
  z.literal('nontaxable'),
])

export const TaxFormsSchema = z.union([
  z.literal('form_1099'),
  z.literal('form_1040'),
  z.literal('form_w2'),
])

export const SpecialEducationFormsSchema = z.union([
  z.literal('form_IEP'),
  z.literal('form_service_plan'),
  z.literal('form_504'),
  z.literal('form_other'),
])

export const RelationshipFormsSchema = z.literal('relation_to_student')

export const FormsSchema = z.union([
  TaxFormsSchema,
  NonTaxableIncomeSchema,
  SpecialEducationFormsSchema,
  RelationshipFormsSchema,
])
export type Forms = z.infer<typeof FormsSchema>

export type NonTaxableIncome = z.infer<typeof NonTaxableIncomeSchema>

export const FilingStatusSchema = z.enum([
  'single',
  'jointly',
  'individually',
  'did_not_file',
  'single',
])

export type FilingStatus = z.infer<typeof FilingStatusSchema>

export const TaxReturnDocuments = z.object({
  form1040: z.string().nullable(),
  form1099: z.string().nullable(),
  formW2: z.string().nullable(),
})

export const UploadedDocument = z.object({
  document_type: TaxFormsSchema.or(NonTaxableIncomeSchema)
    .or(SpecialEducationFormsSchema)
    .or(RelationshipFormsSchema),
  src: z.string(),
  documentMember: z.union([z.literal('user'), z.literal('partner')]),
  submitted: z.boolean().default(false),
})
export type UploadedDocument = z.infer<typeof UploadedDocument>

export const AlternativeDoscSchema = z.object({
  documentName: z.string(),
  url: z.string().url(),
  isAdded: z.boolean().default(false),
})

const MilitarySchema = z.number().transform((val) => {
  if (val === 1) {
    return true
  }

  return false
})

export type ResidencyDocument = z.infer<typeof AlternativeDoscSchema>

export const IncomeEvaluationSchema = z.union([
  z.literal('dws'),
  z.literal('tax_commission'),
  z.literal('manual'),
])
export type IncomeEvaluation = z.infer<typeof IncomeEvaluationSchema>

export const FamilyApplicationRespSchema = z.object({
  family_account_id: z.number(),
  application_id: z.number(),
  applicant_id: z.number(),
  first_name: z.string().nullable(),
  middle_name: z.string().nullable().optional(),
  last_name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  address: z.string().nullable(),
  address2: z.string().nullable().or(z.undefined()),
  city: z.string().nullable(),
  county: z.string().nullable().or(z.undefined()),
  zip_code: z.string().nullable(),
  date_of_birth: z.string().nullable().optional(),
  global_state_id: z.number().nullable(),
  language_preference_id: z.number().nullable(),
  skip_income_verification: z.boolean(),
  household_inhabitants: z.number().nullable(),
  household_income_greater: YesOrNoSchema.nullable(),
  household_income_type: HouseHoldIncomeTypeSchema.nullable(),
  is_military_exemption: MilitarySchema,
  non_taxable_income: z.array(NonTaxableIncomeSchema).nullable(),
  household_filed_federal_income: FilingStatusSchema.nullable(),
  tax_commission_attested_data_by_applicant: z.number().nullable(),
  social_security_number: z.string().nullable().optional(),
  SSN: z.string().nullable().optional(),
  submitted_at: z.string().nullable(),
  students: z.array(StudentSchema).min(0),
  attestations: z.array(FamilyAttestationSchema).min(0),
  documents: z.array(UploadedDocument),
  residency_alternative_documents: z.array(AlternativeDoscSchema).optional(),
  residencyDocument: z.string().url().nullable().optional(),
  flags: z.array(ApplicationFlagSchema).min(0),
  verified_feed_adjustment_gross_income: z.number().nullable(),
  verified_income_tax_year: z.string().nullable(),
  has_benefit: z.boolean().nullable(),
  income_evaluated_by: IncomeEvaluationSchema.nullable(),
  spouse_ssn: z.string().nullable(),
  spouse_first_name: z.string().nullable(),
  spouse_last_name: z.string().nullable(),
  spouse_verified_gross_income: z.number().nullable(),
  spouse_verified_tax_year: z.string().nullable(),
})

export type FamilyApplicationResp = z.infer<typeof FamilyApplicationRespSchema>

export async function GETFamilyApplication(
  program_id: number
): Promise<RequestResponse<FamilyApplicationResp>> {
  try {
    const response = await axiosInstance.get(
      FamilyApplicationAPIs.getUserApplication,
      { params: { program_id, with: 'flags' } }
    )

    return {
      data: FamilyApplicationRespSchema.parse({
        ...response.data,
      }),
      error: null,
    }
  } catch (e) {
    console.error(e)
    return { error: parseRequestError(e), data: null }
  }
}

const POSTApplicantSSNCheckReqSchema = z.object({
  SSN: z.string(),
  program_id: z.number(),
})
const POSTApplicantSSNCheckRespSchema = z.object({
  message: z.string(),
})

type POSTApplicantSSNCheckRequest = z.infer<
  typeof POSTApplicantSSNCheckReqSchema
>
type POSTApplicantSSNCheckResponse = z.infer<
  typeof POSTApplicantSSNCheckRespSchema
>

export async function POSTApplicantSSNCheck(
  payload: POSTApplicantSSNCheckRequest
): Promise<RequestResponse<POSTApplicantSSNCheckResponse>> {
  try {
    const response = await axiosInstance.post(
      FamilyApplicationEndpoints.post_ssn_check,
      payload
    )
    return {
      data: POSTApplicantSSNCheckRespSchema.parse(response.data),
      error: null,
    }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

// Base type that must always be present
type BaseApplication = {
  program_id: number
}

// SSN only type
export type SSNOnly = {
  SSN: string
  spouse_ssn?: string
  date_of_birth?: string
}

// Core application details
export type ApplicationDetails = {
  first_name?: string
  middle_name?: string
  last_name?: string
  address?: string
  address_optional?: string
  city?: string
  global_state_id?: number
  zip_code?: string
  phone?: string
  date_of_birth?: string
}

// Final union type combining all possibilities
export type UpdateApplicationReq =
  | (BaseApplication & SSNOnly)
  | (BaseApplication & ApplicationDetails)

const CreateFamilyApplicationUpdateRespSchema = z.object({
  id: z.number(),
  status: z.number(),
})

export type UpdateApplicantResp = z.infer<
  typeof CreateFamilyApplicationUpdateRespSchema
>

export async function POSTApplicantDetails(
  payload: UpdateApplicationReq
): Promise<RequestResponse<UpdateApplicantResp>> {
  try {
    const response = await axiosInstance.post(
      FamilyApplicationEndpoints.update_family_applicant,
      payload
    )
    return {
      data: CreateFamilyApplicationUpdateRespSchema.parse(response.data),
      error: null,
    }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

export type UpdateIncomeDetailsReq = {
  skip_income_verification?: boolean
  household_inhabitants?: number
  household_income_greater?: YesOrNo
  household_income_type?: string
  non_taxable_income?: NonTaxableIncome[]
  household_filed_federal_income?: string
  program_id: number
}

export type GetTaxCommissionIncomeDetailsReq = {
  application_id: number
  program_id: number
}

export const UpdateIncomeDetailsRespSchema = z.object({
  id: z.number(),
  status: z.number(),
  applicant_id: z.number(),
  program_id: z.number().nullable(),
  phone: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  zip_code: z.string().nullable(),
  global_state_id: z.number().nullable(),
  document_id: z.number().nullable(),
  household_inhabitants: z.number().nullable(),
  household_income_greater: YesOrNoSchema.nullable(),
  household_income_type: z.string().nullable(),
  non_taxable_income: z.array(NonTaxableIncomeSchema).nullable(),
  household_filed_federal_income: FilingStatusSchema.nullable(),
  skip_income_verification: z.boolean(),
})

export type UpdateIncomeDetailsResp = z.infer<
  typeof UpdateIncomeDetailsRespSchema
>

const GetTaxCommissionIncomeDetailsErrorsRespSchema = z.array(
  z.object({
    code: z.number(),
    message: z.string(),
  })
)

export const GetTaxCommissionIncomeDetailsRespSchema = z.array(
  z.object({
    FedAdjustedGrossIncome: z.number(),
    IncomeTaxYear: z.number(),
    NumberofDependents: z.number(),
    Filing: z.string(),
    ResidentStatus: z.string(),
    activity_log_id: z.number(),
    errors: GetTaxCommissionIncomeDetailsErrorsRespSchema,
  })
)

export type GetTaxCommissionIncomeDetailsResp = z.infer<
  typeof GetTaxCommissionIncomeDetailsRespSchema
>

export async function PUTIncomeDetails(
  payload: UpdateIncomeDetailsReq
): Promise<RequestResponse<UpdateIncomeDetailsResp>> {
  try {
    const response = await axiosInstance.put(
      FamilyApplicationEndpoints.update_family_income,
      payload
    )
    return {
      data: UpdateIncomeDetailsRespSchema.parse(response.data),
      error: null,
    }
  } catch (error) {
    console.error(error)
    return { data: null, error: parseRequestError(error) }
  }
}

export async function GetTaxCommissionIncomeDetails(
  payload: GetTaxCommissionIncomeDetailsReq
): Promise<RequestResponse<GetTaxCommissionIncomeDetailsResp>> {
  try {
    const response = await axiosInstance.post(
      FamilyApplicationEndpoints.get_family_income_tax_commission,
      payload
    )
    return {
      data: GetTaxCommissionIncomeDetailsRespSchema.parse(response.data),
      error: null,
    }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

export type PostTaxComissionUserConfirmationReq = {
  application_id: number
  program_id: number
  activity_log_ids: number[]
  tax_commission_attested_data_by_applicant: number
}

export const PostTaxComissionUserConfirmationRespSchema = z.object({
  message: z.string().nullable(),
})

export type PostTaxComissionUserConfirmationResp = z.infer<
  typeof PostTaxComissionUserConfirmationRespSchema
>

export async function PostTaxComissionUserConfirmation(
  payload: PostTaxComissionUserConfirmationReq
): Promise<RequestResponse<PostTaxComissionUserConfirmationResp>> {
  try {
    const response = await axiosInstance.post(
      FamilyApplicationEndpoints.post_tax_commission_user_confirmation,
      payload
    )
    return {
      data: PostTaxComissionUserConfirmationRespSchema.parse(response.data),
      error: null,
    }
  } catch (error) {
    console.error(error)
    return { data: null, error: parseRequestError(error) }
  }
}

export type SetApplicationFlagsRequest = {
  application_id: number
  flag: Flags
  global_application_step_code: string
  student_id?: number
}

export async function SetApplicationFlag({
  application_id,
  flag,
  global_application_step_code,
  student_id,
}: SetApplicationFlagsRequest): Promise<RequestResponse<NoContent>> {
  const endpoint = `/family_application/flags`
  const params = new URLSearchParams({
    application_id: application_id.toString(),
    flag_code: flag,
    global_application_step_code,
  })
  if (student_id) {
    params.append('student_id', student_id.toString())
  }
  try {
    await axiosInstance.post(`${endpoint}?${params}`)
    return { data: '', error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

export type UpdateAcctAttestationReq = {
  family_account_id: number
  attestation: Pick<FamilyAttestation, 'id' | 'status'>
}

export const UpdateAcctAttestationRespSchema = z.object({
  id: z.number(),
  status: z.number(),
  program_attestation_id: z.number(),
})

export type UpdateAcctAttestationResp = z.infer<
  typeof UpdateAcctAttestationRespSchema
>

export async function PUTFamilyAttestation(
  payload: UpdateAcctAttestationReq
): Promise<RequestResponse<UpdateAcctAttestationResp>> {
  try {
    const response = await axiosInstance.put(
      FamilyApplicationEndpoints.update_family_attestation,
      {
        family_account_id: payload.family_account_id,
        attestations: [
          {
            id: payload.attestation.id,
            status: payload.attestation.status,
          },
        ],
      }
    )
    return {
      data: UpdateAcctAttestationRespSchema.parse(response.data),
      error: null,
    }
  } catch (error) {
    const error_message = parseRequestError(error)
    console.log(error_message)
    return { data: null, error: error_message }
  }
}

export async function PUTSubmitApplication(
  application_id: number,
  selected_student_ids: number[]
): Promise<RequestResponse<NoContent>> {
  try {
    const response = await axiosInstance.put(
      FamilyApplicationEndpoints.submit_family_application,
      { application_id, selected_student_ids }
    )

    return { data: response.data, error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

export type WithdrawStudentRequest = {
  application_id: number
  program_id: number
  student_id: number
}

export async function WithdrawStudent(
  payload: WithdrawStudentRequest
): Promise<RequestResponse<NoContent>> {
  try {
    await axiosInstance.post(
      FamilyApplicationEndpoints.withdraw_student,
      payload
    )
    return { data: '', error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

export type ConvertToNewStudentRequest = {
  program_id: number
  student_id: number
}

export async function ConvertToNewStudent(
  payload: ConvertToNewStudentRequest
): Promise<RequestResponse<NoContent>> {
  try {
    await axiosInstance.put(FamilyApplicationEndpoints.convert_to_new, payload)
    return { data: '', error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}
